import { useEffect, useRef, type ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  CalendarBlank,
  MapPin,
  Users,
  WarningCircle,
  SpinnerGap,
  X,
} from "@phosphor-icons/react";
import type { EventSummary } from "../../shared/contracts";
import { useApp } from "../context";
import { date, label } from "../i18n";
export function Status({ value }: { value: string }) {
  const { language } = useApp();
  return (
    <span className={"status status-" + value}>{label(value, language)}</span>
  );
}
export function Loading() {
  const { t } = useApp();
  return (
    <div className="state" role="status">
      <SpinnerGap className="spin" size={24} />
      {t("読み込み中…", "加载中…")}
    </div>
  );
}
export function ErrorState({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  const { t, errorText } = useApp();
  return (
    <div className="state error" role="alert">
      <WarningCircle size={26} />
      <p>{errorText(error)}</p>
      {retry && <button onClick={retry}>{t("再読み込み", "重新加载")}</button>}
    </div>
  );
}
export function Empty({ children }: { children: ReactNode }) {
  return <div className="state empty">{children}</div>;
}
export function PageHeading({
  eyebrow,
  title,
  children,
  action,
}: {
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {children && <p className="muted">{children}</p>}
      </div>
      {action}
    </div>
  );
}
export function EventCard({ event }: { event: EventSummary }) {
  const { t, language } = useApp();
  return (
    <article className="event-card">
      <Link className="card-photo" to={"/events/" + event.id}>
        <img src={event.image} alt="" loading="lazy" />
        <Status value={event.status} />
      </Link>
      <div className="card-content">
        <Link to={"/brands/" + event.brandId} className="brand-link">
          {event.brand.name}
        </Link>
        <Link to={"/events/" + event.id}>
          <h3>{event.title}</h3>
        </Link>
        <p className="card-meta">
          <CalendarBlank size={17} />
          {event.start
            ? date(event.start, language)
            : t(
                `${event.candidates.length}つの候補から日程調整`,
                `${event.candidates.length} 个候选时间征集中`,
              )}
        </p>
        <p className="card-meta">
          <MapPin size={17} />
          {event.venue.name}
          <span className="dot">·</span>
          {label(event.language, language)}
        </p>
        <div className="card-bottom">
          <span>
            {event.price === 0
              ? t("無料", "免费")
              : `¥${event.price.toLocaleString()}`}
          </span>
          <Link to={"/events/" + event.id}>
            {t("詳細を見る", "查看详情")}
            <ArrowRight size={16} />
          </Link>
        </div>
        {event.registration && (
          <div className="personal-state">
            <Users size={16} />
            <Status value={event.registration.status} />
          </div>
        )}
      </div>
    </article>
  );
}
export function RequireAuth({
  children,
  admin = false,
}: {
  children: ReactNode;
  admin?: boolean;
}) {
  const { user, loading, t } = useApp(),
    location = useLocation();
  if (loading) return <Loading />;
  if (!user)
    return (
      <Navigate
        to={
          "/login?return=" +
          encodeURIComponent(location.pathname + location.search)
        }
        replace
      />
    );
  if (admin && user.role !== "admin")
    return <Empty>{t("運営者権限が必要です。", "需要平台管理员权限。")}</Empty>;
  return <>{children}</>;
}
export function Dialog({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useApp();
  useEffect(() => {
    if (open && !ref.current?.open) ref.current?.showModal();
    if (!open && ref.current?.open) ref.current?.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog-heading">
        <h2>{title}</h2>
        <button
          className="icon-button"
          onClick={onClose}
          type="button"
          aria-label={t("閉じる", "关闭")}
        >
          <X size={22} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
